import { AnimatePresence, motion } from 'framer-motion'
import { Loader2 } from 'lucide-react'
import type { Map as MapLibreMap } from 'maplibre-gl'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { EntityTypeFilterPanel } from '@/components/entity-filter/EntityTypeFilterPanel'
import { classifyEntities } from '@/components/geolocation/entity-classification'
import { MapSupportGraph } from '@/components/geolocation/MapSupportGraph'
import { MapCoordHUD } from '@/components/geolocation/map-coord-hud'
import { MapSearchBar } from '@/components/geolocation/map-search-bar'
import { MapToolbar } from '@/components/geolocation/map-toolbar'
import { type CursorInfo, MapLibreViewer } from '@/components/geolocation/maplibre-viewer'
import { EmptyStateInsightsPrompt } from '@/components/investigations/EmptyStateInsightsPrompt'
import { InvestigationSidebar } from '@/components/investigations/InvestigationSidebar'
import { ObjectSetChipsBar } from '@/components/investigations/ObjectSetChipsBar'
import { TracksLayerBadge } from '@/components/investigations/TracksLayerBadge'
import { LABEL_COLORS } from '@/components/neo4j/cytoscape/entity-icons'
import type { SelectedNode } from '@/components/neo4j/cytoscape/types'
import {
  buildDensity,
  fitBounds,
  ModeToggle,
  readTemporalExtent,
  type TemporalMode,
  TimeRangeFilter,
  usePlaybackController,
} from '@/components/time-range-filter'
import { Card } from '@/components/ui/card'
import { useGeoMapData } from '@/features/geolocation/hooks/useServerInvestigationGeoData'
import { SAMPLE_ENTITIES } from '@/features/geolocation/utils/sample-data'
import { useInvestigateEmptyStateProps } from '@/features/investigations/hooks/useInvestigateEmptyStateProps'
import { useObjectSet } from '@/features/investigations/hooks/useObjectSet'
import { snippetSelection } from '@/features/source-snippets/object-set'
import { useIsMobile } from '@/hooks/use-mobile'
import { useInvestigateContextOptional } from '@/pages/cases/InvestigateContext'
import { api } from '@/services/api'
import { fetchPatternUnionLegacy as fetchPatternUnion } from '@/services/api/pattern-query-union'
import { useGeofenceStore } from '@/stores/geofence-store'
import { useGeolocationStore } from '@/stores/geolocation-store'
import { useSelectionStore } from '@/stores/selection-store'
import { useTimelineStore } from '@/stores/timeline-store'
import { cn } from '@/utils/cn'

// Canonical entity-type ordering for the filter panel — same as Graph view.
const LABEL_ORDER = Object.keys(LABEL_COLORS)

// Support-graph split. Desktop: the graph takes the left 40% and the map
// slides right. Phone: two columns are unusable at 375 px, so the graph takes
// the top 45% and the map moves down instead.
const SUPPORT_WIDTH_PCT = 40
const SUPPORT_HEIGHT_PCT_PHONE = 45

function supportSplit(isPhone: boolean, open: boolean) {
  if (isPhone) {
    return {
      paneClass: 'right-0 border-b',
      paneStyle: { height: `${SUPPORT_HEIGHT_PCT_PHONE}%` },
      paneHidden: { y: '-100%' },
      paneShown: { y: 0 },
      mapInset: { top: open ? `${SUPPORT_HEIGHT_PCT_PHONE}%` : '0%', left: '0%' },
    }
  }
  return {
    paneClass: 'bottom-0 border-r',
    paneStyle: { width: `${SUPPORT_WIDTH_PCT}%` },
    paneHidden: { x: '-100%' },
    paneShown: { x: 0 },
    mapInset: { left: open ? `${SUPPORT_WIDTH_PCT}%` : '0%' },
  }
}

export function GeolocationView({ caseId }: { caseId: string }) {
  const [loading, setLoading] = useState(false)
  const [sidebarOpen, setSidebarOpen] = useState(true)
  const [showFilterPanel, setShowFilterPanel] = useState(false)
  const [supportViewOpen, setSupportViewOpen] = useState(false)
  const isPhone = useIsMobile()
  const investigate = useInvestigateContextOptional()
  const activeGraphFilters = investigate?.activeGraphFilters ?? []
  const setEntityMapping = useTimelineStore(s => s.setEntityMapping)
  const setVisibleGraphNodeIds = useTimelineStore(s => s.setVisibleGraphNodeIds)
  const setGlobalTimeRange = useTimelineStore(s => s.setGlobalTimeRange)
  const setTemporalEvents = useTimelineStore(s => s.setTemporalEvents)
  const objectSet = useObjectSet()
  // Defer the WebGL canvas mount by a frame so the previous map (if we
  // arrived from another view that also held a WebGL context) has fully
  // torn down before we create a new one.
  const [canMountMap, setCanMountMap] = useState(false)
  useEffect(() => {
    const raf = requestAnimationFrame(() => {
      requestAnimationFrame(() => setCanMountMap(true))
    })
    return () => cancelAnimationFrame(raf)
  }, [])

  // Map ref + cursor state, plumbed into the HUD overlay components.
  const mapRef = useRef<MapLibreMap | null>(null)
  const [cursor, setCursor] = useState<CursorInfo | null>(null)
  const onMapReady = useCallback((map: MapLibreMap) => {
    mapRef.current = map
  }, [])
  const onCursor = useCallback((info: CursorInfo | null) => setCursor(info), [])
  const openSidebar = useCallback(() => setSidebarOpen(true), [])
  const {
    setEntities,
    setTracks,
    dataSource,
    setAvailableSources,
    selectAllSources,
    setSelectedSourceIds,
    preSelectedDocumentId,
    setPreSelectedDocumentId,
  } = useGeolocationStore()
  const loadGeofences = useGeofenceStore(s => s.loadGeofences)

  // Load persisted geofences
  useEffect(() => {
    if (caseId) loadGeofences(caseId)
  }, [caseId, loadGeofences])

  // Load available geo sources on mount
  useEffect(() => {
    if (!caseId) return
    api.geolocation.getGeoSources(caseId).then(sources => {
      setAvailableSources(sources)
      if (sources.length > 0 && dataSource === 'files') {
        if (preSelectedDocumentId) {
          const match = sources.find(s => s.id === preSelectedDocumentId)
          // Directly set to exactly the matched source (or all as fallback)
          setSelectedSourceIds(new Set(match ? [match.id] : sources.map(s => s.id)))
          setPreSelectedDocumentId(null)
        } else {
          // Idempotent: always sets to all sources regardless of current state
          selectAllSources()
        }
      }
    })
  }, [
    caseId,
    setPreSelectedDocumentId,
    preSelectedDocumentId, // Directly set to exactly the matched source (or all as fallback)
    setSelectedSourceIds,
    setAvailableSources, // Idempotent: always sets to all sources regardless of current state
    selectAllSources,
    dataSource,
  ])

  // Geo entities + tracks come from the active investigation's GraphView
  // snapshot (no network call). The backend `classify()` helper stamps
  // each node with `_render_mode`, `_geo_lat`, `_geo_lon`, etc.; this hook
  // derives the GeoEntity list, tracks, time range, temporal events, and
  // entity-key mapping client-side. Snapshots saved before stamping
  // shipped need a one-time `Sync properties` action from the
  // investigation sidebar; until then the hook returns empty arrays.
  const {
    entities: investigationEntities,
    tracks: investigationTracks,
    globalTimeRange: investigationTimeRange,
    temporalEvents: investigationTemporalEvents,
    keyToNode: investigationKeyToNode,
    nodeToKey: investigationNodeToKey,
    hasActiveInvestigation,
  } = useGeoMapData()

  useEffect(() => {
    if (dataSource !== 'files' || !caseId) return

    // Clear stale per-case state from any previous case (or previous load
    // attempt). The timeline-store's entity mapping is the most important
    // one: an EntityRow / support-graph click resolves to a Neo4j node id
    // through it, and the entity-detail endpoint doesn't validate that
    // the node belongs to the current case — so a stale mapping would
    // render *another case's* properties under this case's entity
    // header. Belt-and-braces: also drop entities, tracks, temporal
    // events and the visible-node set, all of which are case-scoped.
    setEntities(investigationEntities)
    setEntityMapping(investigationKeyToNode, investigationNodeToKey)
    setTracks(investigationTracks)
    setTemporalEvents(investigationTemporalEvents)
    setGlobalTimeRange(investigationTimeRange)
    setVisibleGraphNodeIds(null)
    setLoading(false)
    // Without an active investigation the snapshot-driven path has no
    // data to show. An empty-state prompt elsewhere on the page tells
    // the user how to start one.
    void hasActiveInvestigation
  }, [
    caseId,
    dataSource,
    hasActiveInvestigation,
    investigationEntities,
    investigationTracks,
    investigationTimeRange,
    investigationTemporalEvents,
    investigationKeyToNode,
    investigationNodeToKey,
    setEntities,
    setEntityMapping,
    setTracks,
    setGlobalTimeRange,
    setTemporalEvents,
    setVisibleGraphNodeIds,
  ])

  // Apply active graph filters to the map via visibleGraphNodeIds (same mechanism
  // InvestigationTimeline uses). Null = no filter; Set = only entities whose graph
  // node id is in the set are shown.
  const filterKey = useMemo(
    () =>
      activeGraphFilters
        .map(f => f.id)
        .sort()
        .join('|'),
    [activeGraphFilters]
  )
  // Active Investigation's Object Set drives map visibility:
  //   - Populated investigation → only Object Set entities render
  //   - Empty investigation     → no entity markers (user is prompted to start
  //                               an investigation; the basemap stays visible
  //                               for spatial context)
  // Legacy chip-driven visibility is kept as a fallback when no investigation
  // is active AND chips exist (e.g. an external page set chips).
  useEffect(() => {
    if (objectSet.memberIds !== null) {
      setVisibleGraphNodeIds(objectSet.memberIds)
      return
    }
    if (activeGraphFilters.length === 0) {
      // Empty / bird's-eye: hide all entity markers; the EmptyStatePrompt
      // overlay tells the user how to start.
      setVisibleGraphNodeIds(new Set<string>())
      return
    }
    let cancelled = false
    fetchPatternUnion(caseId, activeGraphFilters)
      .then(res => {
        if (cancelled) return
        setVisibleGraphNodeIds(new Set((res.nodes || []).map((n: any) => n.id)))
      })
      .catch(() => {
        if (!cancelled) setVisibleGraphNodeIds(new Set())
      })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId, filterKey, objectSet.memberIds, setVisibleGraphNodeIds])

  // Clear filter-driven visibility on unmount so other views start clean.
  useEffect(() => {
    return () => setVisibleGraphNodeIds(null)
  }, [setVisibleGraphNodeIds])

  // Load sample data — filter to static only, matching the Geo view scope.
  useEffect(() => {
    if (dataSource !== 'sample') return
    setEntities(SAMPLE_ENTITIES.filter(e => e.isStatic))
  }, [dataSource, setEntities])

  // When the sidebar is open, push the right toolbar left so the panel
  // doesn't cover it. Sidebar is right:16 + width:372 = ~388 from edge,
  // plus 16 px of breathing room → 404.
  // On a phone the sidebar overlays the whole map instead of sitting beside
  // the tools, so there is nothing to clear — and 404 px would push the tool
  // column off a 375 px screen.
  const showEmptyOverlay = objectSet.ids === null
  const sidebarVisible = sidebarOpen && !showEmptyOverlay
  const toolbarRightOffset = sidebarVisible && !isPhone ? 404 : 16
  // The empty-state card fills a phone screen; the search bar, tool column
  // and coordinate HUD would only float on top of its text.
  const showMapChrome = !(showEmptyOverlay && isPhone)

  const emptyStateProps = useInvestigateEmptyStateProps('map')

  // Filter panel state — wired to geolocation-store's hiddenEntityTypes so
  // the MapLibre viewer's existing visibility logic just works. Counts come
  // from the unfiltered entity set so hidden types stay listed and toggleable.
  const storeEntities = useGeolocationStore(s => s.entities)
  const hiddenEntityTypes = useGeolocationStore(s => s.hiddenEntityTypes)
  const toggleEntityTypeVisibility = useGeolocationStore(s => s.toggleEntityTypeVisibility)
  const setHiddenEntityTypes = useGeolocationStore(s => s.setHiddenEntityTypes)
  const timelineVisible = useGeolocationStore(s => s.timelineVisible)

  const nodeTypeStats = useMemo(() => {
    if (!showFilterPanel) return []
    const counts = new Map<string, number>()
    for (const e of storeEntities) {
      const key = e.label ?? e.type ?? 'Unknown'
      counts.set(key, (counts.get(key) ?? 0) + 1)
    }
    return Array.from(counts.entries())
      .sort(([a], [b]) => {
        const ia = LABEL_ORDER.indexOf(a)
        const ib = LABEL_ORDER.indexOf(b)
        const oa = ia === -1 ? LABEL_ORDER.length : ia
        const ob = ib === -1 ? LABEL_ORDER.length : ib
        if (oa !== ob) return oa - ob
        return a.localeCompare(b)
      })
      .map(([type, count]) => ({
        type,
        count,
        isHidden: hiddenEntityTypes.has(type),
        color: LABEL_COLORS[type] ?? { fill: '#64748b', stroke: '#475569' },
      }))
  }, [showFilterPanel, storeEntities, hiddenEntityTypes])

  const hiddenCount = hiddenEntityTypes.size

  // ── Support graph (synced with map) ──────────────────────────────────
  // Mount a `KnowledgeGraphView` to the LEFT of the map when the user opens
  // the support view from the toolbar fan. Selection is mirrored both ways
  // via the same entity-key ↔ node-id mapping the timeline already populates
  // (see InvestigationTimeline for the canonical implementation of this
  // sync — kept identical here so the two callers behave the same).
  const geoSelectedEntityId = useGeolocationStore(s => s.selectedEntityId)
  const entityKeyToNodeId = useTimelineStore(s => s.entityKeyToNodeId)
  const nodeIdToEntityKey = useTimelineStore(s => s.nodeIdToEntityKey)
  const crossSelectRef = useRef(false)
  const [externalGraphNodeId, setExternalGraphNodeId] = useState<string | null>(null)
  const [clearSignal, setClearSignal] = useState(0)

  useEffect(() => {
    if (crossSelectRef.current) {
      crossSelectRef.current = false
      return
    }
    if (geoSelectedEntityId) {
      setExternalGraphNodeId(
        snippetSelection(geoSelectedEntityId)
          ? null
          : (entityKeyToNodeId[geoSelectedEntityId] ?? null)
      )
    } else {
      setExternalGraphNodeId(null)
      setClearSignal(s => s + 1)
    }
  }, [geoSelectedEntityId, entityKeyToNodeId])

  // Mirror map selection into the canonical selection-store so the
  // InvestigationSidebar's matching entity row auto-expands and scrolls into
  // view (the inline entity-detail panel watches this store).
  useEffect(() => {
    const replace = useSelectionStore.getState().replace
    if (!geoSelectedEntityId) {
      replace([], 'map')
      return
    }
    const nodeId = entityKeyToNodeId[geoSelectedEntityId]
    if (nodeId) replace([nodeId], 'map')
  }, [geoSelectedEntityId, entityKeyToNodeId])

  const handleGraphNodeSelected = useCallback(
    (node: SelectedNode | null) => {
      if (!node) {
        crossSelectRef.current = true
        useGeolocationStore.getState().selectEntity(null)
        return
      }
      const entityKey = nodeIdToEntityKey[node.id]
      if (!entityKey) return
      const currentGeoSelection = useGeolocationStore.getState().selectedEntityId
      if (currentGeoSelection !== entityKey) {
        crossSelectRef.current = true
        useGeolocationStore.getState().selectEntity(entityKey)
      }
    },
    [nodeIdToEntityKey]
  )

  // MapLibre needs an explicit resize when its container width changes
  // (the support panel takes/releases the left 40% — the top 45% on a
  // phone). One trailing call after the spring settles is enough. The
  // support view stays open through timeline playback by design — the user
  // wants the graph visible while tracking is running.
  useEffect(() => {
    const t = setTimeout(() => mapRef.current?.resize(), 360)
    return () => clearTimeout(t)
  }, [supportViewOpen])

  const split = supportSplit(isPhone, supportViewOpen)

  // ── Bottom temporal strip (map view) ───────────────────────────────────
  // The strip has two scopes, picked by the Filter/Playback toggle in its
  // left cell (so the control stays in place across mode flips):
  //
  //  - filter   axis spans ALL temporal entities (animated + pinned).
  //             Handles hide entities outside the window.
  //  - playback axis spans only animated entities — the playhead never
  //             sits in dead zones outside the actual tracks. Transport
  //             controls + playhead appear; wheel-scroll zooms.
  //
  // Both modes share `timeline-store.filterStart/filterEnd` as the filter
  // window so the Cesium clock and the maplibre-viewer pipelines pick
  // changes up for free.
  const { animated: animatedEntities, timeAnchored: timeAnchoredEntities } = useMemo(
    () => classifyEntities(storeEntities),
    [storeEntities]
  )
  const hasAnimated = animatedEntities.length > 0
  const hasTimeAnchored = timeAnchoredEntities.length > 0
  const hasTemporalData = hasAnimated || hasTimeAnchored

  const mapTracks = useGeolocationStore(s => s.tracks)

  const mapTemporalModeRaw = useGeolocationStore(s => s.mapTemporalMode)
  const setMapTemporalMode = useGeolocationStore(s => s.setMapTemporalMode)
  // The toggle is only meaningful when both paradigms have data. When the
  // case has only one kind of entity, the strip is pinned to whichever
  // mode actually has axis content (so the user can't accidentally end up
  // on an empty axis).
  const showModeToggle = hasAnimated && hasTimeAnchored
  const mapTemporalMode: TemporalMode = hasAnimated
    ? hasTimeAnchored
      ? mapTemporalModeRaw
      : 'playback'
    : 'filter'

  // Per-entity temporal extent — covers both paradigms. Time-anchored
  // pins read `_temporal_min` / `_temporal_max` from their properties;
  // animated tracks fall back to the actual TrackPoint timestamps.
  const entityExtents = useMemo(() => {
    const out = new Map<string, { start: number; end: number }>()
    if (!hasTemporalData) return out
    for (const entity of timeAnchoredEntities) {
      const ext = readTemporalExtent(entity.properties as Record<string, unknown> | undefined)
      if (ext) out.set(entity.id, ext)
    }
    for (const entity of animatedEntities) {
      const fromProps = readTemporalExtent(entity.properties as Record<string, unknown> | undefined)
      if (fromProps) {
        out.set(entity.id, fromProps)
        continue
      }
      const pts = mapTracks[entity.id]
      if (!pts || pts.length === 0) continue
      let min = Number.POSITIVE_INFINITY
      let max = Number.NEGATIVE_INFINITY
      for (const p of pts) {
        const t = Date.parse(p.timestamp)
        if (!Number.isFinite(t)) continue
        if (t < min) min = t
        if (t > max) max = t
      }
      if (Number.isFinite(min) && Number.isFinite(max)) out.set(entity.id, { start: min, end: max })
    }
    return out
  }, [hasTemporalData, timeAnchoredEntities, animatedEntities, mapTracks])

  // In playback mode the axis covers ONLY animated entities — so the
  // playhead never sits in dead zones. In filter mode the axis covers
  // everything (the user is choosing what to hide, regardless of kind).
  const inPlayback = mapTemporalMode === 'playback'
  const scopedEntities = useMemo(
    () => (inPlayback ? animatedEntities : [...animatedEntities, ...timeAnchoredEntities]),
    [inPlayback, animatedEntities, timeAnchoredEntities]
  )
  const scopedStartTimestamps = useMemo(() => {
    const out: number[] = []
    for (const entity of scopedEntities) {
      const ext = entityExtents.get(entity.id)
      if (ext) out.push(ext.start)
    }
    return out
  }, [scopedEntities, entityExtents])

  const mapTimeBounds = useMemo(() => {
    if (scopedEntities.length === 0) return null
    const allTs: number[] = []
    for (const entity of scopedEntities) {
      const ext = entityExtents.get(entity.id)
      if (ext) allTs.push(ext.start, ext.end)
    }
    return fitBounds(allTs)
  }, [scopedEntities, entityExtents])

  const mapTimeDensity = useMemo(
    () => (mapTimeBounds ? buildDensity(mapTimeBounds, scopedStartTimestamps) : undefined),
    [mapTimeBounds, scopedStartTimestamps]
  )

  // Canonical filter window — `timeline-store.filterStart/filterEnd`.
  // Read it as ms for the component, write it back as ISO. Cesium clock
  // and the maplibre-viewer pipelines already key off this slice, so a
  // single write powers playback boundaries + pin/track culling at once.
  const filterStartIso = useTimelineStore(s => s.filterStart)
  const filterEndIso = useTimelineStore(s => s.filterEnd)
  const setFilterWindow = useTimelineStore(s => s.setFilterWindow)

  const filterValue = useMemo(() => {
    if (!filterStartIso || !filterEndIso) return null
    const start = Date.parse(filterStartIso)
    const end = Date.parse(filterEndIso)
    if (!Number.isFinite(start) || !Number.isFinite(end)) return null
    return { start, end }
  }, [filterStartIso, filterEndIso])

  const applyFilterChange = useCallback(
    (range: { start: number; end: number } | null) => {
      if (!range) {
        if (mapTimeBounds) {
          setFilterWindow(
            new Date(mapTimeBounds.start).toISOString(),
            new Date(mapTimeBounds.end).toISOString()
          )
        } else {
          setFilterWindow(null, null)
        }
        return
      }
      setFilterWindow(new Date(range.start).toISOString(), new Date(range.end).toISOString())
    },
    [mapTimeBounds, setFilterWindow]
  )

  // Auto-clear filter if the case data shifted under it.
  useEffect(() => {
    if (!filterValue || !mapTimeBounds) return
    const outside = filterValue.end < mapTimeBounds.start || filterValue.start > mapTimeBounds.end
    if (outside) setFilterWindow(null, null)
  }, [filterValue, mapTimeBounds, setFilterWindow])

  // Visible count: entities in the current scope whose extent overlaps the
  // active filter window. Entities with no extent stay counted — a missing
  // timestamp is never grounds to hide.
  const mapVisibleCount = useMemo(() => {
    if (!filterValue) return scopedEntities.length
    let n = 0
    for (const entity of scopedEntities) {
      const ext = entityExtents.get(entity.id)
      if (!ext) {
        n += 1
        continue
      }
      if (ext.end >= filterValue.start && ext.start <= filterValue.end) n += 1
    }
    return n
  }, [filterValue, scopedEntities, entityExtents])

  // Playback controller — only mounted when the case actually has
  // animated tracks, so we don't run the Cesium clock loop in pure-pin
  // cases. `mapTimeBounds` is passed as fallback so Play works the first
  // time the user clicks it (no Auto-fit needed) and survives a
  // filter→playback toggle that leaves the store filter window null.
  const playback = usePlaybackController(mapTimeBounds)

  // Scroll-zoom viewWindow (only meaningful for playback, which is the
  // only context where you want a sub-slice of the filter range).
  const viewStartIso = useTimelineStore(s => s.viewStart)
  const viewEndIso = useTimelineStore(s => s.viewEnd)
  const setViewWindow = useTimelineStore(s => s.setViewWindow)
  const viewWindow = useMemo(() => {
    if (!viewStartIso || !viewEndIso) return undefined
    const start = Date.parse(viewStartIso)
    const end = Date.parse(viewEndIso)
    if (!Number.isFinite(start) || !Number.isFinite(end)) return undefined
    return { start, end }
  }, [viewStartIso, viewEndIso])
  const applyViewWindow = useCallback(
    (next: { start: number; end: number } | null) => {
      if (!next) setViewWindow(null, null)
      else setViewWindow(new Date(next.start).toISOString(), new Date(next.end).toISOString())
    },
    [setViewWindow]
  )

  const showStrip = hasTemporalData && timelineVisible

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <ObjectSetChipsBar view="map" />
      <div className="flex-1 overflow-hidden relative min-h-0">
        {loading && (
          <div className="absolute top-2 left-2 z-30">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        )}

        <AnimatePresence initial={false}>
          {supportViewOpen && (
            <motion.aside
              key="support-graph"
              initial={split.paneHidden}
              animate={split.paneShown}
              exit={split.paneHidden}
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
              className={cn(
                'absolute top-0 left-0 z-20 overflow-hidden border-border bg-card',
                split.paneClass
              )}
              style={split.paneStyle}
            >
              <MapSupportGraph
                caseId={caseId}
                onNodeSelected={handleGraphNodeSelected}
                externalSelectedNodeId={externalGraphNodeId}
                onExternalNodeHandled={() => setExternalGraphNodeId(null)}
                externalClearSelectionSignal={clearSignal}
              />
            </motion.aside>
          )}
        </AnimatePresence>

        <motion.div
          animate={split.mapInset}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          className="absolute top-0 right-0 bottom-0"
        >
          {canMountMap ? (
            <>
              <MapLibreViewer
                onMapReady={onMapReady}
                onCursor={onCursor}
                // On a phone the sidebar starts collapsed (it overlays the
                // whole map), so a tapped pin would only glow: bring the
                // sidebar back with the pin's detail.
                onEntityPick={isPhone ? openSidebar : undefined}
              />
              {showMapChrome && (
                <>
                  <MapSearchBar mapRef={mapRef} />
                  <MapToolbar
                    mapRef={mapRef}
                    rightOffset={toolbarRightOffset}
                    sidebarOpen={sidebarOpen}
                    onOpenSidebar={openSidebar}
                    showFilterPanel={showFilterPanel}
                    onToggleFilter={() => setShowFilterPanel(prev => !prev)}
                    supportViewOpen={supportViewOpen}
                    onToggleSupportView={() => setSupportViewOpen(v => !v)}
                  />
                  <MapCoordHUD cursor={cursor} />
                </>
              )}
              <TracksLayerBadge />
              <EntityTypeFilterPanel
                open={showFilterPanel}
                stats={nodeTypeStats}
                hiddenCount={hiddenCount}
                onToggle={toggleEntityTypeVisibility}
                onShowAll={() => setHiddenEntityTypes(new Set())}
                onClose={() => setShowFilterPanel(false)}
                rightOffset={toolbarRightOffset + 56}
                topOffset={12}
              />
              {!showEmptyOverlay && (
                <InvestigationSidebar
                  caseId={caseId}
                  view="map"
                  open={sidebarOpen}
                  onOpenChange={setSidebarOpen}
                />
              )}
              {showEmptyOverlay && (
                // Phone: the card fills the map, so it sits above MapLibre's
                // own controls (z-index 2) — the attribution pill would
                // otherwise cover the card's bottom row.
                <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-background/40 backdrop-blur-[1px] max-md:z-10">
                  <Card
                    variant="floating"
                    className="pointer-events-auto w-full max-w-5xl rounded-2xl p-0 shadow-xl"
                  >
                    <EmptyStateInsightsPrompt view="map" {...emptyStateProps} />
                  </Card>
                </div>
              )}
            </>
          ) : (
            <div className="h-full flex items-center justify-center">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          )}
        </motion.div>
      </div>
      {/* Bottom temporal strip — same component for both modes. The Filter/
          Playback toggle lives in the strip's left cell (via `leftSlot`)
          so the control doesn't move when the user switches. In playback
          mode the axis covers only animated entities so the playhead
          never sits in dead zones; in filter mode it spans everything. */}
      {showStrip && mapTimeBounds && (
        <TimeRangeFilter
          // On a phone the open sidebar covers the whole map, so the strip
          // steps aside and leaves its height to the sidebar.
          className={sidebarVisible ? 'max-md:hidden' : undefined}
          view="map"
          bounds={mapTimeBounds}
          value={filterValue}
          onChange={applyFilterChange}
          density={mapTimeDensity}
          timestamps={scopedStartTimestamps}
          total={scopedEntities.length}
          visibleCount={mapVisibleCount}
          onAutoFit={() => applyFilterChange(null)}
          countSuffix={inPlayback ? 'tracked' : 'pins'}
          playback={inPlayback ? playback : undefined}
          zoomable={inPlayback}
          viewWindow={inPlayback ? viewWindow : undefined}
          onViewWindowChange={inPlayback ? applyViewWindow : undefined}
          leftSlot={
            showModeToggle ? (
              <ModeToggle value={mapTemporalMode} onChange={setMapTemporalMode} />
            ) : undefined
          }
        />
      )}
    </div>
  )
}
